import { type QueryClient, queryOptions, useMutation } from '@tanstack/react-query'
import type {
  AccountStatus,
  ApplyAccess,
  CreateAccount,
  CreateSubaccount,
  SetEntitlement,
  SetSeats,
  UpdateAccount,
} from '@headline/schemas'
import { api } from './api'

/**
 * Accounts and sub-accounts — the tenant scaffold. `staleTime` stays at the 30s default: this
 * page is the only editor, and every mutation below invalidates.
 */
export const accountsQuery = queryOptions({
  queryKey: ['accounts'],
  queryFn: async () => {
    const res = await api.api.accounts.$get()
    if (!res.ok) throw new Error('Failed to load accounts')
    return res.json()
  },
})

/**
 * The workspace-first launcher's data — the sub-accounts this user may enter, for the non-admin
 * Accounts page. Internal admins get an empty list from the server and keep the account-first
 * launcher, so this is only fetched for them by mistake, never rendered.
 */
export const myWorkspacesQuery = queryOptions({
  queryKey: ['me', 'workspaces'],
  queryFn: async () => {
    const res = await api.api.me.workspaces.$get()
    if (!res.ok) throw new Error('Failed to load your workspaces')
    return res.json()
  },
})

export function accountQuery(accountId: string) {
  return queryOptions({
    queryKey: ['accounts', accountId],
    queryFn: async () => {
      const res = await api.api.accounts[':accountId'].$get({ param: { accountId } })
      if (!res.ok) {
        const body = await res.json()
        throw new Error('error' in body ? body.error.message : 'Failed to load the account')
      }
      return res.json()
    },
  })
}

export function subaccountsQuery(accountId: string) {
  return queryOptions({
    queryKey: ['accounts', accountId, 'subaccounts'],
    queryFn: async () => {
      const res = await api.api.accounts[':accountId'].subaccounts.$get({ param: { accountId } })
      if (!res.ok) {
        const body = await res.json()
        throw new Error('error' in body ? body.error.message : 'Failed to load sub-accounts')
      }
      return res.json()
    },
  })
}

/**
 * A sub-account's effective entitlements — the account's, inherited (products are set once at the
 * account level). Read through the SUB-ACCOUNT so a workspace-only user, who can't see the parent
 * account, can still learn what its topics may run on — the topic wizard uses it to lock un-bought
 * data sources.
 */
export function subaccountEntitlementsQuery(accountId: string, subaccountId: string) {
  return queryOptions({
    queryKey: ['accounts', accountId, 'subaccounts', subaccountId, 'entitlements'],
    queryFn: async () => {
      const res = await api.api.accounts[':accountId'].subaccounts[
        ':subaccountId'
      ].entitlements.$get({ param: { accountId, subaccountId } })
      if (!res.ok) {
        const body = await res.json()
        throw new Error('error' in body ? body.error.message : 'Failed to load entitlements')
      }
      return res.json()
    },
  })
}

/** One invalidation for the family — a sub-account change moves the parent's count too. */
function invalidateAccounts(queryClient: QueryClient) {
  return queryClient.invalidateQueries({ queryKey: ['accounts'] })
}

export function useCreateAccount(queryClient: QueryClient) {
  return useMutation({
    mutationFn: async (input: CreateAccount) => {
      const res = await api.api.accounts.$post({ json: input })
      if (!res.ok) {
        const body = await res.json()
        throw new Error('error' in body ? body.error.message : 'Failed to create the account')
      }
      return res.json()
    },
    onSuccess: () => invalidateAccounts(queryClient),
  })
}

export function useUpdateAccount(queryClient: QueryClient) {
  return useMutation({
    mutationFn: async (input: { accountId: string } & UpdateAccount) => {
      const { accountId, ...body } = input
      const res = await api.api.accounts[':accountId'].$patch({ param: { accountId }, json: body })
      if (!res.ok) {
        const payload = await res.json()
        throw new Error('error' in payload ? payload.error.message : 'Failed to update the account')
      }
      return res.json()
    },
    onSuccess: () => invalidateAccounts(queryClient),
  })
}

/**
 * What still runs under an account, for the warning before it is marked inactive. Always fresh:
 * the warning is only worth reading if it says what is true now.
 */
export function accountRunningQuery(accountId: string) {
  return queryOptions({
    queryKey: ['accounts', accountId, 'running'],
    staleTime: 0,
    // Failing shows only Cancel, so say so straight away rather than after a round of retries.
    retry: false,
    queryFn: async () => {
      const res = await api.api.accounts[':accountId'].running.$get({ param: { accountId } })
      if (!res.ok) {
        const body = await res.json()
        throw new Error('error' in body ? body.error.message : 'Failed to read what is running')
      }
      return res.json()
    },
  })
}

/** Sets the label and, if asked, pauses what runs: so every report and RTA view changes too. */
export function useSetAccountStatus(queryClient: QueryClient) {
  return useMutation({
    mutationFn: async (input: {
      accountId: string
      status: AccountStatus
      pauseRunning?: boolean
    }) => {
      const { accountId, ...body } = input
      const res = await api.api.accounts[':accountId'].status.$post({
        param: { accountId },
        json: body,
      })
      if (!res.ok) {
        const payload = await res.json()
        throw new Error('error' in payload ? payload.error.message : 'Failed to change the status')
      }
      return res.json()
    },
    onSuccess: () =>
      Promise.all([
        invalidateAccounts(queryClient),
        queryClient.invalidateQueries({ queryKey: ['reports-overview'] }),
        queryClient.invalidateQueries({ queryKey: ['reports'] }),
        queryClient.invalidateQueries({ queryKey: ['report'] }),
        queryClient.invalidateQueries({ queryKey: ['rtas'] }),
        queryClient.invalidateQueries({ queryKey: ['workspace-api'] }),
      ]),
  })
}

/**
 * Deletes an account and everything under it. Clears the whole cache rather than invalidating
 * the accounts key: sub-accounts, entitlements and access lists are all keyed by an id that no
 * longer resolves, and refetching them would just produce 404s on the way out of the page.
 */
export function useDeleteAccount(queryClient: QueryClient) {
  return useMutation({
    mutationFn: async (accountId: string) => {
      const res = await api.api.accounts[':accountId'].$delete({ param: { accountId } })
      if (!res.ok) {
        const body = await res.json()
        throw new Error('error' in body ? body.error.message : 'Failed to delete the account')
      }
      return res.json()
    },
    onSuccess: () => queryClient.clear(),
  })
}

export function useCreateSubaccount(queryClient: QueryClient) {
  return useMutation({
    mutationFn: async (input: { accountId: string } & CreateSubaccount) => {
      const { accountId, ...body } = input
      const res = await api.api.accounts[':accountId'].subaccounts.$post({
        param: { accountId },
        json: body,
      })
      if (!res.ok) {
        const payload = await res.json()
        throw new Error(
          'error' in payload ? payload.error.message : 'Failed to create the sub-account',
        )
      }
      return res.json()
    },
    onSuccess: () => invalidateAccounts(queryClient),
  })
}

export function useRenameSubaccount(queryClient: QueryClient) {
  return useMutation({
    mutationFn: async (input: { accountId: string; subaccountId: string; name: string }) => {
      const res = await api.api.accounts[':accountId'].subaccounts[':subaccountId'].$patch({
        param: { accountId: input.accountId, subaccountId: input.subaccountId },
        json: { name: input.name },
      })
      if (!res.ok) {
        const payload = await res.json()
        throw new Error('error' in payload ? payload.error.message : 'Failed to rename')
      }
      return res.json()
    },
    onSuccess: () => invalidateAccounts(queryClient),
  })
}

/** One sub-account setting at a time — today the LexisNexis full-text switch. */

export function useDeleteSubaccount(queryClient: QueryClient) {
  return useMutation({
    mutationFn: async (input: { accountId: string; subaccountId: string }) => {
      const res = await api.api.accounts[':accountId'].subaccounts[':subaccountId'].$delete({
        param: input,
      })
      if (!res.ok) {
        const payload = await res.json()
        throw new Error(
          'error' in payload ? payload.error.message : 'Failed to remove the sub-account',
        )
      }
      return res.json()
    },
    onSuccess: () => invalidateAccounts(queryClient),
  })
}

/** Turning a feature on or off for one account — internal admins only. */
export function useSetEntitlement(queryClient: QueryClient) {
  return useMutation({
    mutationFn: async (input: { accountId: string } & SetEntitlement) => {
      const { accountId, ...body } = input
      const res = await api.api.accounts[':accountId'].entitlements.$put({
        param: { accountId },
        json: body,
      })
      if (!res.ok) {
        const payload = await res.json()
        throw new Error(
          'error' in payload ? payload.error.message : 'Failed to update the entitlement',
        )
      }
      return res.json()
    },
    onSuccess: () => invalidateAccounts(queryClient),
  })
}

export function useSetAccountSeats(queryClient: QueryClient) {
  return useMutation({
    mutationFn: async (input: { accountId: string } & SetSeats) => {
      const { accountId, ...body } = input
      const res = await api.api.accounts[':accountId'].seats.$put({
        param: { accountId },
        json: body,
      })
      if (!res.ok) {
        const payload = await res.json()
        throw new Error('error' in payload ? payload.error.message : 'Failed to set the seat cap')
      }
      return res.json()
    },
    onSuccess: () => invalidateAccounts(queryClient),
  })
}

export function useSetSubaccountSeats(queryClient: QueryClient) {
  return useMutation({
    mutationFn: async (input: { accountId: string; subaccountId: string } & SetSeats) => {
      const { accountId, subaccountId, ...body } = input
      const res = await api.api.accounts[':accountId'].subaccounts[':subaccountId'].seats.$put({
        param: { accountId, subaccountId },
        json: body,
      })
      if (!res.ok) {
        const payload = await res.json()
        throw new Error('error' in payload ? payload.error.message : 'Failed to set the seat cap')
      }
      return res.json()
    },
    onSuccess: () => invalidateAccounts(queryClient),
  })
}

/**
 * The person-first view of one account tree — the account, its sub-accounts, everyone's holdings
 * on each, pending invitations, and the time-boxed grants in force. One request, because the
 * permission picker opens on a PERSON and needs every resource's answer at once; asking
 * per-resource would repeat the identity join once per sub-account.
 */
export function accessOverviewQuery(accountId: string) {
  return queryOptions({
    queryKey: ['rbac', 'resource-access', 'overview', accountId],
    queryFn: async () => {
      const res = await api.api.rbac['resource-access'].overview.$get({ query: { accountId } })
      if (!res.ok) {
        const body = await res.json()
        throw new Error('error' in body ? body.error.message : 'Failed to load access')
      }
      return res.json()
    },
  })
}

/**
 * The picker's save: one person's access across the account tree, applied as a diff — additions
 * and removals in one request. Somebody who isn't a member yet gets ONE invitation covering every
 * entry, which is the reason this can't be a client-side loop over single-resource grants.
 */
export function useApplyAccess(queryClient: QueryClient) {
  return useMutation({
    mutationFn: async (input: ApplyAccess) => {
      const res = await api.api.rbac['resource-access'].apply.$post({ json: input })
      if (!res.ok) {
        const body = await res.json()
        throw new Error('error' in body ? body.error.message : 'Failed to apply the changes')
      }
      return res.json()
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['rbac'] }),
  })
}

/**
 * Every account tree at once — the Access control page's person-first view. Gated on the
 * org-wide `org-users:manage`, so only internal admins can load it; the account page keeps
 * using the account-rooted overview above.
 */
export const orgAccessOverviewQuery = queryOptions({
  queryKey: ['rbac', 'resource-access', 'org-overview'],
  queryFn: async () => {
    const res = await api.api.rbac['resource-access']['org-overview'].$get()
    if (!res.ok) {
      const body = await res.json()
      throw new Error('error' in body ? body.error.message : 'Failed to load access')
    }
    return res.json()
  },
})

/**
 * The Access control picker's save: the apply endpoint is rooted at one account tree, so a diff
 * spanning several accounts becomes one apply per touched account. Only offered for existing
 * members (the Users tab has no add-by-email picker), so no invitations ride on these — the
 * one-invitation-per-person promise stays a per-tree guarantee.
 */
export function useApplyAccessBatch(queryClient: QueryClient) {
  return useMutation({
    mutationFn: async (applies: ApplyAccess[]) => {
      // Concurrent: each apply names a different account tree, and the server guards each one
      // independently. A partial failure surfaces as the error; the refetch shows what landed.
      return Promise.all(
        applies.map(async (input) => {
          const res = await api.api.rbac['resource-access'].apply.$post({ json: input })
          if (!res.ok) {
            const body = await res.json()
            throw new Error('error' in body ? body.error.message : 'Failed to apply the changes')
          }
          return res.json()
        }),
      )
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['rbac'] }),
  })
}

export function useCancelPendingGrant(queryClient: QueryClient) {
  return useMutation({
    mutationFn: async (pendingId: string) => {
      const res = await api.api.rbac['resource-access'].pending[':pendingId'].$delete({
        param: { pendingId },
      })
      if (!res.ok) {
        const body = await res.json()
        throw new Error('error' in body ? body.error.message : 'Failed to cancel')
      }
      return res.json()
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['rbac'] }),
  })
}

/** Removes an assignment by id — the escape hatch for a role no capability names. */
export function useRevokeResourceRole(queryClient: QueryClient) {
  return useMutation({
    mutationFn: async (input: {
      assignmentId: string
      membershipId: string
      /** The resource the assignment is on — what the API authorizes, and checks it matches. */
      resourceType: string
      resourceId: string
    }) => {
      const res = await api.api.rbac['resource-access'][':assignmentId'].$delete({
        param: { assignmentId: input.assignmentId },
        query: {
          membershipId: input.membershipId,
          // Overview holdings carry the type as a plain string; the API's validator is what
          // rejects anything that isn't an account or sub-account.
          resourceType: input.resourceType as 'account' | 'subaccount',
          resourceId: input.resourceId,
        },
      })
      if (!res.ok) {
        const body = await res.json()
        throw new Error('error' in body ? body.error.message : 'Failed to revoke access')
      }
      return res.json()
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['rbac'] }),
  })
}

/**
 * The staff-only record of which dashboards are vetted. Under `accounts` so that `invalidateAccounts`
 * refreshes it; a customer's screen never asks for it, and the route would refuse her if it did.
 */
export function dashboardVettingQuery(accountId: string) {
  return queryOptions({
    queryKey: ['accounts', accountId, 'dashboard-vetting'],
    queryFn: async () => {
      const res = await api.api.accounts[':accountId']['dashboard-vetting'].$get({
        param: { accountId },
      })
      if (!res.ok) {
        const body = await res.json()
        throw new Error('error' in body ? body.error.message : 'Failed to load dashboard vetting')
      }
      return res.json()
    },
  })
}

export function useSetDashboardVetted(queryClient: QueryClient) {
  return useMutation({
    mutationFn: async (input: { accountId: string; subaccountId: string; vetted: boolean }) => {
      const { vetted, ...param } = input
      const res = await api.api.accounts[':accountId'].subaccounts[':subaccountId'][
        'dashboard-vetted'
      ].$put({ param, json: { vetted } })
      if (!res.ok) {
        const body = await res.json()
        throw new Error('error' in body ? body.error.message : 'Failed to change the switch')
      }
      return res.json()
    },
    onSuccess: () => invalidateAccounts(queryClient),
  })
}

export function useSetLegacySubscriptions(queryClient: QueryClient) {
  return useMutation({
    mutationFn: async (input: { accountId: string; subaccountId: string; ids: string[] }) => {
      const { ids, ...param } = input
      const res = await api.api.accounts[':accountId'].subaccounts[':subaccountId'][
        'legacy-subscriptions'
      ].$put({ param, json: { ids } })
      if (!res.ok) {
        const body = await res.json()
        throw new Error('error' in body ? body.error.message : 'Failed to save the subscriptions')
      }
      return res.json()
    },
    onSuccess: () => invalidateAccounts(queryClient),
  })
}

/** Legacy subscriptions by name or exact id, from a submitted search (never per keystroke). */
export function legacySubscriptionSearchQuery(q: string) {
  return queryOptions({
    queryKey: ['legacy-subscriptions', q],
    queryFn: async () => {
      const res = await api.api['legacy-subscriptions'].$get({ query: { q } })
      if (!res.ok) {
        const body = await res.json()
        throw new Error('error' in body ? body.error.message : 'Failed to search subscriptions')
      }
      return res.json()
    },
  })
}
